import React, { useContext, useState, useEffect } from "react";
import { defineMessages, useIntl } from "react-intl";

import { StepperContext } from "@natera/stepper";
import Tooltip from "@natera/material/lib/tooltip";
import { Button } from "@natera/form";

import { StepperContent } from "@app/components/ui/layout/stepperContent";
import StepperHeaderProgress from "@app/components/stepperHeaderProgress";
import FinancialAssistanceIcon from "@app/preBill/assets/svg/financial-assistance.svg";
import { InformationalActionCard } from "@app/preBill/components/informationalActionCard";
import { PreBillAPIContext, ConfigContext } from "@app/provider";
import { getFormattedLocalizeDate, openNewWindow } from "@app/utils";
import { IntlContext } from "@app/provider";
import { getTestName } from "@app/preBill/utils/getTestName";
import { PaymentChoice, SendPaymentChoiceInput } from "@app/preBill/models";
import { ButtonNoShadow } from "@app/components/buttonNoShadow";
import { OutOfNetworkStepsType } from "../../outOfNetworkStepper";
import { SpinnerView } from "@app/components";

import "./oonCostInformation.scss";
import { formatAmountInDollars } from "@app/preBill/utils";
import { usePreBillHeapEvent } from "@app/preBill/hooks";
import { HEAP_EVENTS } from "@app/provider/types";
import { logMsg } from "@app/utils/logMsg";

const messages = defineMessages({
  oonCostInformationTitle: {
    id: "oonCostInformationTitle",
    defaultMessage: "Your {testName} cost estimate",
  },
  oonCostInformationSubtitle: {
    id: "oonCostInformationSubtitle",
    defaultMessage:
      "Natera is <b>not</b> in-network with your insurance plan. As a result, we're unable to provide an accurate cost estimate for your test.",
  },
  oonCostInformationEstimateTitle: {
    id: "oonCostInformationEstimateTitle",
    defaultMessage: "Average out-of-pocket cost",
  },
  oonCostInformationEstimateTitleTooltip: {
    id: "oonCostInformationEstimateTitleTooltip",
    defaultMessage:
      "Average out-of-pocket cost for patients with similar health insurance coverage who have met their deductible.",
  },
  oonCostInformationStatusTitle: {
    id: "oonCostInformationStatusTitle",
    defaultMessage: "Status",
  },
  oonCostInformationStatusValue: {
    id: "oonCostInformationStatusValue",
    defaultMessage: "Out-of-Network",
  },
  oonCostInformationInsurancePlanTitle: {
    id: "oonCostInformationInsurancePlanTitle",
    defaultMessage: "Insurance plan",
  },
  oonCostInformationText: {
    id: "oonCostInformationText",
    defaultMessage: "We'll continue with submitting your claim to insurance.",
  },
  oonCostInformationContinueButton: {
    id: "oonCostInformationContinueButton",
    defaultMessage: "Continue with Insurance",
  },
  oonCostInformationBackButton: {
    id: "oonCostInformationBackButton",
    defaultMessage: "Back",
  },
  otherPaymentOptionsSectionTitle: {
    id: "otherPaymentOptionsSectionTitle",
    defaultMessage: "Other payment options",
  },
  financialAssistanceTitle: {
    id: "financialAssistanceTitle",
    defaultMessage: "Learn about financial assistance",
  },
  financialAssistanceText: {
    id: "financialAssistanceText",
    defaultMessage:
      "You may qualify to pay as little as $0 — applying takes just a few minutes.",
  },
  payCashNowTitle: {
    id: "payCashNowTitle",
    defaultMessage: "Pay cash now",
  },
  payCashNowText: {
    id: "payCashNowText",
    defaultMessage: "If you prefer to not use your insurance.",
  },
  billingCostInformationEstimateTitleDate: {
    id: "billingCostInformationEstimateTitleDate",
    defaultMessage: "Estimate date",
  },
  billingCostInformationEstimateTitleDateTooltip: {
    id: "billingCostInformationEstimateTitleDateTooltip",
    defaultMessage:
      "This estimate reflects your benefits as of the date we requested them from your health plan. Future services may impact your deductible, coinsurance, or copay, which could change your final out-of-pocket costs.",
  },
});

interface OonCostProps {
  onPaymentSelect: (
    paymentSelect: PaymentChoice.CASHPAY | PaymentChoice.INSURANCE
  ) => void;
}

const OonCostInformation: React.FC<OonCostProps> = ({ onPaymentSelect }) => {
  const intl = useIntl();
  const { goBack, resolve, goAt, reset } = React.useContext(StepperContext);
  const { currentLanguage } = useContext(IntlContext);
  const {
    apiProviderState: { orderEstimate },
    sendPaymentChoice,
  } = useContext(PreBillAPIContext);
  const { config } = useContext(ConfigContext);
  const [testName, setTestName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const { trackPreBillHeapEvent } = usePreBillHeapEvent();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setTestName(getTestName(orderEstimate?.testType, intl));
  }, [orderEstimate?.testType, intl]);

  const createOopCostText = () => {
    const { oonAverageOopMin, oonAverageOopMax } = orderEstimate ?? {};

    if (!oonAverageOopMin || !oonAverageOopMax) return formatAmountInDollars(0);

    return `${formatAmountInDollars(
      oonAverageOopMin
    )} - ${formatAmountInDollars(oonAverageOopMax)}`;
  };

  const handleGotIt = async () => {
    const payload: SendPaymentChoiceInput = {
      paymentChoice: PaymentChoice.INSURANCE,
    };
    try {
      setLoading(true);
      trackPreBillHeapEvent({
        eventKey: HEAP_EVENTS.upp_pb_click_estimate_got_it,
      });
      await sendPaymentChoice(payload);
      onPaymentSelect(PaymentChoice.INSURANCE);
      resolve({});
      goAt(OutOfNetworkStepsType.BILLING_END_INFORMATION);
    } catch {
      logMsg("error", "prebill.estimate", "Error sending payment choice", {
        flow: "out-of-network",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    reset();
    goBack();
  };

  const handleFinancialAssistance = () => {
    trackPreBillHeapEvent({ eventKey: HEAP_EVENTS.upp_pb_click_compcare });
    openNewWindow(config.preBill.contactUsChatbot);
  };

  const formattedSubtitle = intl.formatMessage(
    messages.oonCostInformationSubtitle,
    { b: (...chunks) => <b>{chunks}</b> }
  );

  const handleCashPayButton = () => {
    trackPreBillHeapEvent({ eventKey: HEAP_EVENTS.upp_pb_click_pay_cash });
    onPaymentSelect(PaymentChoice.CASHPAY);
    resolve({});
    goAt(OutOfNetworkStepsType.OON_CASH_PAY);
  };

  return (
    <>
      <SpinnerView isLoading={loading} />
      <StepperHeaderProgress />
      <div className="oon-cost-information">
        <StepperContent
          title={intl.formatMessage(messages.oonCostInformationTitle, {
            testName: testName,
          })}
        >
          <p className="oon-cost-information__subtitle">{formattedSubtitle}</p>
          <div className="oon-cost-information__estimate">
            <div className="oon-cost-information__estimate-section">
              <div className="oon-cost-information__estimate-section__title">
                <span className="oon-cost-information__estimate-section__header">
                  {intl.formatMessage(messages.oonCostInformationEstimateTitle)}
                </span>
              </div>
              <span className="oon-cost-information__estimate-section__cost">
                {createOopCostText() || `$0`}
              </span>
              <span className="oon-cost-information__estimate-section__average-info">
                {intl.formatMessage(
                  messages.oonCostInformationEstimateTitleTooltip
                )}
              </span>
            </div>
            <div className="oon-cost-information__estimate-section">
              <div className="oon-cost-information__estimate-section__title">
                <span className="oon-cost-information__estimate-section__header">
                  {intl.formatMessage(messages.oonCostInformationStatusTitle)}
                </span>
              </div>
              <span className="oon-cost-information__estimate-section__information">
                {intl.formatMessage(messages.oonCostInformationStatusValue)}
              </span>
            </div>
            <div className="oon-cost-information__estimate-section">
              <div className="oon-cost-information__estimate-section__title">
                <span className="oon-cost-information__estimate-section__header">
                  {intl.formatMessage(
                    messages.oonCostInformationInsurancePlanTitle
                  )}
                </span>
              </div>
              <span className="oon-cost-information__estimate-section__information">
                {orderEstimate?.insuranceCompanyName}
              </span>
            </div>
            <div className="oon-cost-information__estimate-section">
              <div className="oon-cost-information__estimate-section__title">
                <span className="oon-cost-information__estimate-section__header">
                  {intl.formatMessage(
                    messages.billingCostInformationEstimateTitleDate
                  )}
                </span>
                <Tooltip
                  content={intl.formatMessage(
                    messages.billingCostInformationEstimateTitleDateTooltip
                  )}
                >
                  <div className="oon-cost-estimate-tooltip">?</div>
                </Tooltip>
              </div>
              <span className="oon-cost-information__estimate-section__information">
                {getFormattedLocalizeDate(
                  orderEstimate?.insuranceEstimateDate,
                  currentLanguage
                )}
              </span>
            </div>
          </div>
          <div className="oon-cost-information__text">
            <span className="oon-cost-information__text__info">
              {intl.formatMessage(messages.oonCostInformationText)}
            </span>
          </div>
        </StepperContent>
        <div className="stepper__actions">
          <ButtonNoShadow
            raised
            onClick={handleGotIt}
            data-testid="oon-cost-information__got-it-button"
          >
            {intl.formatMessage(messages.oonCostInformationContinueButton)}
          </ButtonNoShadow>
          <Button
            onClick={handleBack}
            data-testid="oon-cost-information__back-button"
            className="back-button"
          >
            {intl.formatMessage(messages.oonCostInformationBackButton)}
          </Button>
        </div>
        <div className="oon-cost-information__divider" />
        <div className="other-payment-section">
          <h3 className="other-payment-section__title">
            {intl.formatMessage(messages.otherPaymentOptionsSectionTitle)}
          </h3>
          <InformationalActionCard
            icon={FinancialAssistanceIcon}
            title={intl.formatMessage(messages.financialAssistanceTitle)}
            text={intl.formatMessage(messages.financialAssistanceText)}
            onClick={handleFinancialAssistance}
          />
          <InformationalActionCard
            icon={FinancialAssistanceIcon}
            title={intl.formatMessage(messages.payCashNowTitle)}
            text={intl.formatMessage(messages.payCashNowText)}
            onClick={handleCashPayButton}
          />
        </div>
      </div>
    </>
  );
};

export default OonCostInformation;
